import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { displayLocalizedAssetType } from "#src/utils/display";
import { linkToAssetDetailPage } from "#src/utils/links";
import { useQuery } from "@tanstack/react-query";
import {
  DataTable,
  HeaderType,
  KeyValuePanel,
  Panel,
} from "@validereinc/common-components";
import {
  AssetAdapter,
  AssetSchemaType,
  AssetTypeType,
} from "@validereinc/domain";
import React from "react";

export const WorkflowTemplateAssetTable = ({
  assetType,
  assetsQueryFilters,
}: {
  assetType?: AssetTypeType;
  assetsQueryFilters?: Parameters<typeof AssetAdapter.getList>[0]["filters"];
}) => {
  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["workflow-template-assets"]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "id",
      sortDirection: "asc",
    },
    undefined,
    tableStateStorageKey
  );
  const assetsQuery = useQuery({
    queryKey: ["assets", assetsQueryFilters, tableState],
    queryFn: () =>
      AssetAdapter.getList({
        page: tableState.page,
        pageSize: tableState.pageSize,
        sortBy: tableState.sortBy,
        sortDirection: tableState.sortDirection,
        filters: {
          ...assetsQueryFilters,
          isAlreadyFormatted: true,
        },
      }),
    enabled: Boolean(assetsQueryFilters),
  });
  const assetDisplayString = displayLocalizedAssetType(assetType);

  const headers: Array<HeaderType<AssetSchemaType>> = [
    {
      label: "Name",
      key: "name",
      isSortable: false,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToAssetDetailPage(item.asset_type, item.id)}>
          {item.name}
        </RoutingLink>
      ),
    },
  ];

  if (!assetsQueryFilters) {
    return null;
  }

  return (
    <Panel
      title={`Applicable ${assetDisplayString}`}
      isFluidY={false}
    >
      <KeyValuePanel
        panelKeyValueListProps={{
          maxRowCount: 1,
          variant: "shaded",
        }}
        panelProps={{
          title: "",
          isFluidY: false,
          style: {
            marginBottom: 8,
            marginTop: 8,
            padding: 0,
            border: "none",
            backgroundColor: "none",
          },
        }}
        panelMaxColumnCount={2}
        panelKeyValueListContainerProps={{
          style: { padding: 0 },
        }}
        data={[
          {
            title: `# of ${assetDisplayString}`,
            value: assetsQuery.data?.total_entries ?? 0,
          },
        ]}
      />
      <DataTable
        headers={headers}
        items={assetsQuery.data?.data ?? []}
        getItemId={(item) => item.id}
        isLoading={assetsQuery.isLoading}
        pagination={{
          pageSize: tableState.pageSize,
          page: tableState.page,
          total: assetsQuery.data?.total_entries,
          showPageSizeControls: false,
          showCurrentPageControls: false,
        }}
        onSortChange={updateTableState}
        onPaginationChange={updateTableState}
      />
    </Panel>
  );
};
